import { AssetType } from 'models/asset/asset';
import { AIActionBinding, AIActionRequest, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import {
  entityExplainAuditTemplate,
  entitySuggestImprovementTemplate,
  entitySummarizeSelectionTemplate,
} from 'models/copilot/actions/aiEntityActionTemplates';
import { processStartTemplate, sceneHelpTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotGenericContent } from 'models/copilot/copilotGeneric';
import { CopilotScene } from 'models/copilot/copilotScene';
import { TenantProcess } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import {
  assetAdditionalDetailsTemplate,
  controlEvidenceGapsTemplate,
  controlPlanCheckTemplate,
  dashboardRecordEventTemplate,
  dashboardSummarizeWorkTemplate,
  kpiAnalyzeTemplate,
  kpiReportStartTemplate,
  objectiveSuggestKpisTemplate,
  riskGapAnalysisTemplate,
  tasksFromReportTemplate,
  tasksWorkloadTemplate,
} from './genericSceneAIActionTemplates';

// The general knowledge skills these bindings name (REDLAB.ISOPlanner.AI/Skills in the API)
const sceneHelpSkill = 'scene-help';
const summarizeSelectionSkill = 'summarize-selection';
const createTaskSkill = 'create-task';
const summarizeMyWorkSkill = 'summarize-my-work';
const tasksFromReportSkill = 'tasks-from-report';
const riskGapAnalysisSkill = 'risk-gap-analysis';
const controlEvidenceGapsSkill = 'control-evidence-gaps';
const suggestImprovementSkill = 'suggest-improvement';

/**
 * The one help question of a scene: what the page is for, the state of its data and what to do next. It is
 * the first action of every usage that offers it, before the usage's process starts.
 */
export const getSceneHelpBinding = (): AIActionBinding =>
  createAIActionBinding(sceneHelpTemplate, undefined, { skill: sceneHelpSkill });

/**
 * The help question first, the rest in their own order: for a usage that lists its process starts before a
 * scene's bindings that already hold the help question.
 */
export const withSceneHelpFirst = (bindings: AIActionBinding[]): AIActionBinding[] => [
  ...bindings.filter((binding) => binding.template === sceneHelpTemplate),
  ...bindings.filter((binding) => binding.template !== sceneHelpTemplate),
];

/**
 * Create tasks from the findings of a report: the task scenes and the Library, after the help question. The
 * Library definition binds it through here, so the skill name lives in one place.
 */
export const getTasksFromReportBinding = (): AIActionBinding =>
  createAIActionBinding(tasksFromReportTemplate, undefined, {
    skill: tasksFromReportSkill,
    usesSelectedDocument: true,
  });

// The KPI report process start of the KPIs scene (PLAN-070); the selected KPIs travel with the scene menu's request
const kpiReportStart = (): AIActionBinding =>
  createAIActionBinding(kpiReportStartTemplate, undefined) as AIActionBinding;

/**
 * One finished JSON data report handed from the data report modal to the KPI report process: the same action the
 * scene lists, started with the job, so step 1 asks only for the instructions.
 */
export const getKPIReportVisualizeRequest = (jobId: number): AIActionRequest => ({
  binding: kpiReportStart(),
  processStartInput: { jobId },
});

const controlEvidenceGaps = (): AIActionBinding =>
  createAIActionBinding(controlEvidenceGapsTemplate, undefined, { skill: controlEvidenceGapsSkill });

const summarizeSelection = (scene: CopilotScene): AIActionBinding =>
  createAIActionBinding(entitySummarizeSelectionTemplate, { scene }, { skill: summarizeSelectionSkill });

/**
 * The ready-made questions of a list scene or the dashboard, in menu order: the help question first. The
 * summarize binding needs rows selected, so only the scene menu lists it, and only while rows are selected.
 */
export const getSceneBindings = (scene: CopilotScene): AIActionBinding[] => {
  switch (scene) {
    case CopilotScene.Dashboard:
      return [
        getSceneHelpBinding(),
        createAIActionBinding(dashboardSummarizeWorkTemplate, undefined, { skill: summarizeMyWorkSkill }),
        createAIActionBinding(dashboardRecordEventTemplate, undefined, { skill: createTaskSkill }),
      ];
    case CopilotScene.Risks:
      return [
        getSceneHelpBinding(),
        createAIActionBinding(riskGapAnalysisTemplate, undefined, { skill: riskGapAnalysisSkill }),
        summarizeSelection(scene),
      ];
    case CopilotScene.Processes:
      return [getSceneHelpBinding(), summarizeSelection(scene)];
    case CopilotScene.Objectives:
      return [getSceneHelpBinding(), summarizeSelection(scene)];
    case CopilotScene.KPIs:
      //the KPI report process: with selected rows their ids start it; without, its card asks for the KPIs
      return [getSceneHelpBinding(), kpiReportStart()];
    case CopilotScene.MyTasks:
    case CopilotScene.MyRequests:
      return [getSceneHelpBinding(), getTasksFromReportBinding(), summarizeSelection(scene)];
    case CopilotScene.AllTasks:
      return [
        getSceneHelpBinding(),
        getTasksFromReportBinding(),
        createAIActionBinding(tasksWorkloadTemplate, undefined),
        summarizeSelection(scene),
      ];
    case CopilotScene.Assets:
      return [getSceneHelpBinding(), summarizeSelection(scene)];
    case CopilotScene.Standards:
      return [getSceneHelpBinding(), controlEvidenceGaps()];
    case CopilotScene.Audits:
      return [getSceneHelpBinding()];
    case CopilotScene.Requirements:
    case CopilotScene.Controls:
      return [getSceneHelpBinding(), controlEvidenceGaps(), summarizeSelection(scene)];
    case CopilotScene.ProcessingActivities:
      return [getSceneHelpBinding(), summarizeSelection(scene)];
    default:
      return [];
  }
};

/**
 * The ready-made questions of a detail scene, the help question first, Draft automation for a control or
 * requirement and Audit with AI for an asset, risk, process, objective or processing activity when the scene judged
 * them available, the empty fields of an asset filled in, and the two investigations of a Supplier asset (PLAN-073),
 * the compliance check only when the scene judged it available (PLAN-CTI-098). A task has none: the task modal's
 * assistant pane binds the task.
 */
export const getEntityDetailBindings = (
  entityType: EntityTypes,
  entityId: number,
  canDraftAutomation?: boolean,
  canAuditEvidence?: boolean,
  assetType?: number,
  canCheckSupplierCompliance?: boolean,
): AIActionBinding[] => {
  const explainAudit = createAIActionBinding(entityExplainAuditTemplate, { entityType });
  const suggestImprovement = createAIActionBinding(
    entitySuggestImprovementTemplate,
    { entityType },
    { skill: suggestImprovementSkill },
  );
  // the drafting process (phase 27) runs keyed on the entity the definition's getEntityRef names
  const draftAutomation =
    canDraftAutomation && entityId
      ? [createAIActionBinding(processStartTemplate, { processId: TenantProcess.AutomationDrafting })]
      : [];
  // the evidence audit (PLAN-077) runs keyed on the entity as well
  const auditEvidence =
    canAuditEvidence && entityId
      ? [createAIActionBinding(processStartTemplate, { processId: TenantProcess.EntityEvidenceAudit })]
      : [];

  switch (entityType) {
    case EntityTypes.Asset:
      // the investigations run keyed on the asset the definition's getEntityRef names; any asset that is a Supplier;
      // the compliance check reads its rows from the audit criteria, so it needs the Audit preview as well
      return [
        getSceneHelpBinding(),
        createAIActionBinding(assetAdditionalDetailsTemplate, undefined),
        ...(assetType === AssetType.Supplier && entityId
          ? [createAIActionBinding(processStartTemplate, { processId: TenantProcess.SupplierIncidents })]
          : []),
        ...(assetType === AssetType.Supplier && entityId && canCheckSupplierCompliance
          ? [createAIActionBinding(processStartTemplate, { processId: TenantProcess.SupplierCompliance })]
          : []),
        ...auditEvidence,
      ];
    case EntityTypes.Risk:
    case EntityTypes.Process:
    case EntityTypes.ProcessingActivity:
      return [getSceneHelpBinding(), ...auditEvidence];
    case EntityTypes.Objective:
      return [getSceneHelpBinding(), createAIActionBinding(objectiveSuggestKpisTemplate, undefined), ...auditEvidence];
    case EntityTypes.KPI:
      return [getSceneHelpBinding(), createAIActionBinding(kpiAnalyzeTemplate, undefined)];
    case EntityTypes.Requirement:
      return [getSceneHelpBinding(), explainAudit, suggestImprovement, ...draftAutomation];
    case EntityTypes.Control:
      return [
        getSceneHelpBinding(),
        explainAudit,
        suggestImprovement,
        createAIActionBinding(controlPlanCheckTemplate, undefined),
        ...draftAutomation,
      ];
    default:
      return [];
  }
};

// The Generic usage's bindings: a detail scene's entity, else a list scene's scene
export const getGenericSceneBindings = (content: CopilotGenericContent): AIActionBinding[] => {
  if (content.entityType !== undefined && content.entityId) {
    return getEntityDetailBindings(
      content.entityType,
      content.entityId,
      content.canDraftAutomation,
      content.canAuditEvidence,
      content.assetType,
      content.canCheckSupplierCompliance,
    );
  }

  if (content.scene !== undefined) return getSceneBindings(content.scene);

  return [];
};
